// =============================================
// AppLog Storage — IndexedDB (Dexie) backend dla globalnego loggera
// Async, ring buffer (max 1000 entries / 7 dni TTL), search/filter friendly.
// =============================================

import Dexie, { type Table } from "dexie";

export type LogCategory =
  | "content-engine"
  | "map-repair"
  | "audit"
  | "existing-posts"
  | "publishing"
  | "auth"
  | "api"
  | "ui"
  | "system";

export type LogLevel = "PUBLIC" | "DEBUG" | "ERROR" | "WARN";

export interface LogEntryRow {
  id?: number;
  ts: string;
  level: LogLevel;
  category: LogCategory;
  prefix: string;
  msg: string;
  data?: unknown;
  runId?: string;
}

class AppLogDB extends Dexie {
  entries!: Table<LogEntryRow, number>;

  constructor() {
    super("AppLogs");
    this.version(1).stores({
      // Indexed: ts (for time range), level/category (for filters), runId (for run grouping)
      entries: "++id, ts, level, category, runId",
    });
  }
}

// Lazy init — Dexie konstruktor sięga do globalnego `indexedDB`, którego nie ma w Node.
// Worker importujący ten moduł przez @/services/contentEngine/contentLogger DOSTANIE
// no-op stub. Wszystkie persist/query funkcje mają guard `typeof window`.
const hasIndexedDB =
  typeof window !== "undefined" && typeof (globalThis as any).indexedDB !== "undefined";

export const logDB: AppLogDB = hasIndexedDB
  ? new AppLogDB()
  : ({ entries: { add: async () => {}, orderBy: () => null } } as unknown as AppLogDB);

// Ring buffer policy
const MAX_ENTRIES_DEFAULT = 1000;
const MAX_ENTRIES_DEBUG = 5000;
const TTL_DAYS = 7;

let insertCounter = 0;
const PRUNE_EVERY_N_INSERTS = 100;

function getMaxEntries(): number {
  try {
    const debugMode =
      (typeof import.meta !== "undefined" &&
        (import.meta as { env?: { VITE_LOG_LEVEL?: string } }).env
          ?.VITE_LOG_LEVEL) === "debug" ||
      (typeof window !== "undefined" &&
        localStorage.getItem("app_log_level") === "debug");
    return debugMode ? MAX_ENTRIES_DEBUG : MAX_ENTRIES_DEFAULT;
  } catch {
    return MAX_ENTRIES_DEFAULT;
  }
}

/** Persist a single log entry to IndexedDB (fire-and-forget). */
export async function persistEntry(entry: Omit<LogEntryRow, "id">): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    await logDB.entries.add(entry);
    insertCounter++;
    if (insertCounter >= PRUNE_EVERY_N_INSERTS) {
      insertCounter = 0;
      void pruneOldEntries();
    }
  } catch (err) {
    // IndexedDB quota / privacy mode → silently fail
    if (typeof console !== "undefined") {
      console.warn("[AppLogStorage] persist failed:", err);
    }
  }
}

export interface QueryOptions {
  category?: LogCategory[];
  level?: LogLevel[];
  search?: string;
  since?: Date;
  runId?: string;
  limit?: number;
}

/** Query entries with filters (newest first). */
export async function queryEntries(opts: QueryOptions = {}): Promise<LogEntryRow[]> {
  if (typeof window === "undefined") return [];
  try {
    let collection = logDB.entries.orderBy("ts").reverse();

    if (opts.since) {
      const sinceIso = opts.since.toISOString();
      collection = collection.filter((e) => e.ts >= sinceIso);
    }
    if (opts.category && opts.category.length > 0) {
      const catSet = new Set(opts.category);
      collection = collection.filter((e) => catSet.has(e.category));
    }
    if (opts.level && opts.level.length > 0) {
      const lvlSet = new Set(opts.level);
      collection = collection.filter((e) => lvlSet.has(e.level));
    }
    if (opts.runId) {
      collection = collection.filter((e) => e.runId === opts.runId);
    }
    if (opts.search) {
      const q = opts.search.toLowerCase();
      collection = collection.filter((e) => {
        if (e.prefix.toLowerCase().includes(q)) return true;
        if (e.msg.toLowerCase().includes(q)) return true;
        if (e.data) {
          try {
            return JSON.stringify(e.data).toLowerCase().includes(q);
          } catch {
            return false;
          }
        }
        return false;
      });
    }

    if (opts.limit) collection = collection.limit(opts.limit);
    return await collection.toArray();
  } catch (err) {
    if (typeof console !== "undefined") {
      console.warn("[AppLogStorage] query failed:", err);
    }
    return [];
  }
}

/** Count entries matching filters. */
export async function countEntries(opts: QueryOptions = {}): Promise<number> {
  if (typeof window === "undefined") return 0;
  try {
    if (!opts.category && !opts.level && !opts.search && !opts.since && !opts.runId) {
      return await logDB.entries.count();
    }
    const matched = await queryEntries(opts);
    return matched.length;
  } catch {
    return 0;
  }
}

/** Auto-cleanup: usuń entries starsze niż TTL_DAYS LUB powyżej limitu. */
export async function pruneOldEntries(): Promise<number> {
  if (typeof window === "undefined") return 0;
  let deleted = 0;
  try {
    // Delete by TTL
    const cutoff = new Date(Date.now() - TTL_DAYS * 24 * 60 * 60 * 1000).toISOString();
    deleted += await logDB.entries.where("ts").below(cutoff).delete();

    // Delete oldest above limit
    const total = await logDB.entries.count();
    const max = getMaxEntries();
    if (total > max) {
      const overflow = total - max;
      const oldest = await logDB.entries.orderBy("ts").limit(overflow).toArray();
      const ids = oldest.map((e) => e.id).filter((id): id is number => typeof id === "number");
      if (ids.length > 0) {
        await logDB.entries.bulkDelete(ids);
        deleted += ids.length;
      }
    }
    return deleted;
  } catch (err) {
    if (typeof console !== "undefined") {
      console.warn("[AppLogStorage] prune failed:", err);
    }
    return 0;
  }
}

/** Clear ALL entries (UI button "Wyczyść wszystkie"). */
export async function clearAllEntries(): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    await logDB.entries.clear();
  } catch (err) {
    if (typeof console !== "undefined") {
      console.warn("[AppLogStorage] clear failed:", err);
    }
  }
}

/** One-time migration: jeśli istnieje localStorage['ce_log_runs'] → import do IndexedDB → clear. */
export async function migrateFromLocalStorage(): Promise<{ imported: number; skipped: boolean }> {
  if (typeof window === "undefined") return { imported: 0, skipped: true };
  const LS_KEY = "ce_log_runs";
  const MIGRATION_FLAG = "app_logs_migrated_v1";

  try {
    if (localStorage.getItem(MIGRATION_FLAG) === "true") {
      return { imported: 0, skipped: true };
    }
    const stored = localStorage.getItem(LS_KEY);
    if (!stored) {
      localStorage.setItem(MIGRATION_FLAG, "true");
      return { imported: 0, skipped: true };
    }

    interface LegacyRun {
      runId: string;
      meta: Record<string, unknown>;
      startedAt: string;
      endedAt?: string;
      entries: Array<{
        ts: string;
        level: LogLevel;
        prefix: string;
        msg: string;
        data?: unknown;
      }>;
    }

    const parsed: LegacyRun[] = JSON.parse(stored);
    let imported = 0;
    for (const run of parsed) {
      const rows: Omit<LogEntryRow, "id">[] = run.entries.map((e) => ({
        ts: e.ts,
        level: e.level,
        category: "content-engine",
        prefix: e.prefix,
        msg: e.msg,
        data: e.data,
        runId: run.runId,
      }));
      if (rows.length > 0) {
        await logDB.entries.bulkAdd(rows);
        imported += rows.length;
      }
    }
    localStorage.removeItem(LS_KEY);
    localStorage.setItem(MIGRATION_FLAG, "true");
    return { imported, skipped: false };
  } catch (err) {
    if (typeof console !== "undefined") {
      console.warn("[AppLogStorage] migration failed:", err);
    }
    return { imported: 0, skipped: true };
  }
}

/** Trigger migration on module load (debug + prod, idempotent). */
if (typeof window !== "undefined") {
  void migrateFromLocalStorage();
  // Also run prune on startup to clean stale entries
  void pruneOldEntries();
}

/** Format entries as TXT (for download). */
export function formatEntriesAsTxt(entries: LogEntryRow[]): string {
  const lines: string[] = [];
  lines.push("=".repeat(70));
  lines.push(`PBN-SD Application Log Export — ${new Date().toISOString()}`);
  lines.push(`Entries: ${entries.length}`);
  lines.push("=".repeat(70));
  lines.push("");

  for (const e of entries) {
    const time = e.ts.replace("T", " ").slice(0, 23);
    const lvl = e.level.padEnd(6);
    const cat = `[${e.category}]`.padEnd(18);
    let line = `${time}  ${lvl} ${cat} [${e.prefix}] ${e.msg}`;
    if (e.data && typeof e.data === "object" && Object.keys(e.data as object).length > 0) {
      try {
        line += "\n  data: " + JSON.stringify(e.data, null, 2).split("\n").join("\n  ");
      } catch {
        line += "\n  data: <unserializable>";
      }
    }
    lines.push(line);
  }

  lines.push("");
  lines.push("=".repeat(70));
  lines.push(`END OF LOG — ${entries.length} entries`);
  return lines.join("\n");
}

/** Trigger browser download of filtered entries. */
export function downloadEntriesAsTxt(entries: LogEntryRow[], filename?: string): void {
  if (typeof window === "undefined") return;
  const txt = formatEntriesAsTxt(entries);
  const blob = new Blob([txt], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download =
    filename ||
    `pbnsd-log-${new Date().toISOString().slice(0, 19).replace(/[:]/g, "-")}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Trigger browser download of filtered entries as JSON. */
export function downloadEntriesAsJson(entries: LogEntryRow[], filename?: string): void {
  if (typeof window === "undefined") return;
  const blob = new Blob([JSON.stringify(entries, null, 2)], {
    type: "application/json;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download =
    filename ||
    `pbnsd-log-${new Date().toISOString().slice(0, 19).replace(/[:]/g, "-")}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
